import type { BadgeTone } from '@/lib/utils/badgeTone'

import { formatDate } from './format'
import type {
  ReservationBoardSlotView,
  ReservationCalendarDayView,
  ReservationListItemView,
  VenueReservationStatus,
} from './reservationTypes'

/**
 * 예약 화면 공통 순수 함수(설계 §2) — 5버킷 집계·상태색 맵·기간 검증·필터·잔여시간 포맷.
 * `closureViewModel.ts` 와 같은 규칙 — `if (status === ...)` 를 컴포넌트에 흩지 않는다.
 */

export interface ReservationDayBuckets {
  /** `CONFIRMED` + `COMPLETED`. */
  completed: number
  held: number
  pending: number
  open: number
  blocked: number
}

/**
 * 캘린더 5버킷 집계 — api-spec §1 "요구 5버킷 매핑"을 그대로 구현한다.
 * 🚨 `CANCELED`/`CANCEL_REQUESTED` 는 이 화면에 노출하지 않는다 — 취소는 슬롯을 점유하지 않아
 * 격자 축과 다르다(서버 계약이 명시. `CANCELED` 는 이 EP 에서 항상 0).
 */
export function summarizeDayBuckets(day: ReservationCalendarDayView): ReservationDayBuckets {
  const countOf = (status: VenueReservationStatus): number =>
    day.statusCounts.find((entry) => entry.status === status)?.count ?? 0

  return {
    completed: countOf('CONFIRMED') + countOf('COMPLETED'),
    held: countOf('HELD'),
    pending: countOf('PENDING'),
    open: day.openSlotCount,
    blocked: day.blockedSlotCount,
  }
}

/**
 * 목록·상세 상태 배지 색(텍스트는 항상 서버 `statusLabel`) — 기존 `Badge` 의 4톤(`BadgeTone`) 안에서
 * 매핑한다(`HELD`·`PENDING` 은 둘 다 "진행 중" 성격이라 같은 톤을 공유하고 문구로 구분한다).
 */
export const RESERVATION_STATUS_TONE: Record<VenueReservationStatus, BadgeTone> = {
  HELD: 'warning',
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCEL_REQUESTED: 'danger',
  CANCELED: 'neutral',
  COMPLETED: 'neutral',
}

/**
 * 예약 상태 필터 선택지(forward-only) — 서버 응답이 오기 전에 필터 UI 를 그려야 해서 이 카탈로그를
 * 쓴다(`CLOSURE_TYPE_CATALOG`/`COURT_STATUS_CATALOG` 와 같은 기존 저장소 관례). 실제 레코드의
 * 상태 문구는 항상 서버 `statusLabel` 을 그대로 쓴다.
 */
export const RESERVATION_STATUS_OPTIONS: { value: VenueReservationStatus; label: string }[] = [
  { value: 'HELD', label: '임시 점유' },
  { value: 'PENDING', label: '확인 중' },
  { value: 'CONFIRMED', label: '확정' },
  { value: 'CANCEL_REQUESTED', label: '취소 요청 중' },
  { value: 'CANCELED', label: '취소' },
  { value: 'COMPLETED', label: '이용 완료' },
]

export interface ReservationPeriod {
  /** "yyyy-MM-dd" */
  from: string
  to: string
}

const MAX_PERIOD_DAYS = 62

function daysBetween(from: string, to: string): number {
  const fromMs = Date.parse(`${from}T00:00:00Z`)
  const toMs = Date.parse(`${to}T00:00:00Z`)
  return Math.round((toMs - fromMs) / 86_400_000) + 1
}

/** #666 api-spec §0-4 기간 규약(필수·역전 금지·최대 62일)을 클라이언트에서 먼저 막는다. */
export function validateReservationPeriod(period: ReservationPeriod): string | null {
  if (period.from.length === 0 || period.to.length === 0) return '조회 기간을 선택해 주세요.'
  if (period.to < period.from) return '종료일은 시작일과 같거나 이후여야 합니다.'
  if (daysBetween(period.from, period.to) > MAX_PERIOD_DAYS) {
    return `조회 기간은 최대 ${MAX_PERIOD_DAYS}일까지 선택할 수 있습니다.`
  }
  return null
}

/** 목록 화면의 기본 조회 기간 — `today` 부터 최근 7일(오늘 포함). */
export function defaultRecentPeriod(today: string): ReservationPeriod {
  const fromMs = Date.parse(`${today}T00:00:00Z`) - 6 * 86_400_000
  const from = new Date(fromMs).toISOString().slice(0, 10)
  return { from, to: today }
}

/**
 * 임시 점유 잔여시간 — "N분 M초"(정적 표시, 설계 §2-7 D2). 🚫 TTL 을 하드코딩하지 않는다 —
 * 응답의 `holdRemainingSeconds` 값만 포맷한다.
 */
export function formatHoldRemaining(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || seconds <= 0) return '곧 해제'
  const minutes = Math.floor(seconds / 60)
  const remainSeconds = seconds % 60
  return minutes > 0 ? `${minutes}분 ${remainSeconds}초` : `${remainSeconds}초`
}

export interface ReservationListFilter {
  from: string
  to: string
  courtId: number | 'ALL'
  /** 빈 배열 = 전체 상태. */
  statuses: VenueReservationStatus[]
}

/** 목록·현황판 API 요청 쿼리 파라미터 — `undefined` 필드는 axios 가 자동으로 뺀다. */
export function reservationListQueryParams(
  filter: ReservationListFilter,
  cursor?: string
): Record<string, string | number | undefined> {
  return {
    from: filter.from,
    to: filter.to,
    courtId: filter.courtId === 'ALL' ? undefined : filter.courtId,
    statuses: filter.statuses.length > 0 ? filter.statuses.join(',') : undefined,
    cursor,
  }
}

/** 코트별 완료·임시 점유 건수 — 현황판(b) 슬롯을 훑어 계산한다(요구사항 4의 코트별 요약용). */
export function summarizeCourtSlots(
  slots: { state: 'OPEN' | 'BLOCKED' | 'RESERVED'; reservationStatus: string | null }[]
): { completed: number; held: number } {
  let completed = 0
  let held = 0
  for (const slot of slots) {
    if (slot.state !== 'RESERVED') continue
    if (slot.reservationStatus === 'CONFIRMED' || slot.reservationStatus === 'COMPLETED') completed += 1
    if (slot.reservationStatus === 'HELD') held += 1
  }
  return { completed, held }
}

/** 목록 화면에서 "선택 시간대 요약"(코트별 현황판) 을 보여줄지 판정 — 기간이 하루로 좁혀졌을 때만. */
export function isSingleDayFilter(filter: Pick<ReservationListFilter, 'from' | 'to'>): boolean {
  return filter.from === filter.to
}

/** 목록·상세 공용 — 예약자 이름 표시(마스킹값 그대로, 파기 여부만 구분). */
export function bookerNameText(item: Pick<ReservationListItemView, 'bookerName' | 'bookerPurged'>): string {
  if (item.bookerPurged) return '(파기됨)'
  return item.bookerName ?? '-'
}

/** 목록·상세 공용 — 예약자 연락처 표시(마스킹값 그대로, 파기 여부만 구분). */
export function bookerPhoneText(item: Pick<ReservationListItemView, 'bookerPhone' | 'bookerPurged'>): string {
  if (item.bookerPurged) return '(파기됨)'
  return item.bookerPhone ?? '-'
}

/** 이용 완료 90일 보관 정책 고정 안내(#588 §C14 — 문구·상수를 함께 바꾸지 않는다). */
const DEFAULT_PURGE_NOTICE = '개인정보는 이용 완료 90일 후 자동 삭제돼요.'

/**
 * 예약 상세 — 개인정보 삭제 안내 캡션(#679). 파기 여부·삭제 예정일(`purgeDueAt`)에 따라 문구를 가른다.
 * 🚨 `purgeDueAt` 이 없는 경우(방어적 케이스)는 기존 고정 문구로 폴백한다 — 읽기 강건성(C11)을 깨지 않는다.
 */
export function bookerPurgeNoticeText(
  reservation: Pick<ReservationListItemView, 'bookerPurged' | 'purgeDueAt'>
): string {
  if (reservation.bookerPurged) return '보관 기간 만료로 개인정보가 삭제됐어요.'
  if (reservation.purgeDueAt) return `개인정보는 ${formatDate(reservation.purgeDueAt)} 이후 자동 삭제돼요.`
  return DEFAULT_PURGE_NOTICE
}

// ---------------------------------------------------------------------------
// 범례 색상 — Badge 의 4톤(`BadgeTone`) 만으로는 5버킷·4유형을 다 구분할 수 없어(설계 §2-4),
// 이 화면 전용 색 축(`ReservationSwatchTone`)을 별도로 둔다. 실제 CSS 클래스는 각 컴포넌트가
// Tailwind 기존 색 토큰(brand·success·warning·slateish)으로 직접 매핑한다.
// ---------------------------------------------------------------------------

export type ReservationSwatchTone = 'success' | 'warning' | 'brand' | 'neutralLight' | 'neutralDark'

/** 캘린더 날짜 셀의 범례 A(5버킷) — Figma 요구사항 2 의 5색 그대로. */
export const DAY_BUCKET_LEGEND: { key: keyof ReservationDayBuckets; label: string; tone: ReservationSwatchTone }[] = [
  { key: 'completed', label: '예약 완료', tone: 'success' },
  { key: 'held', label: '임시 점유 중', tone: 'warning' },
  { key: 'pending', label: '예약 확인 중', tone: 'brand' },
  { key: 'open', label: '예약 가능', tone: 'neutralLight' },
  { key: 'blocked', label: '휴무·차단', tone: 'neutralDark' },
]

/** 현황판(코트×시간 그리드) 칸의 범례 B(4유형) — Figma `예약 캘린더`(13989-43050) 실물과 1:1. */
export type ReservationBoardCellKind = 'APP' | 'MANUAL' | 'LESSON' | 'BLOCKED'

export const BOARD_CELL_LEGEND: { key: ReservationBoardCellKind; label: string; tone: ReservationSwatchTone }[] = [
  { key: 'APP', label: '앱 예약', tone: 'brand' },
  { key: 'MANUAL', label: '전화·현장', tone: 'warning' },
  { key: 'LESSON', label: '레슨', tone: 'success' },
  { key: 'BLOCKED', label: '차단·휴무', tone: 'neutralDark' },
]

/**
 * 현황판 슬롯 한 칸이 범례 B 의 어느 유형인가. `state='OPEN'` 은 빈칸이라 범례 밖(`null`).
 * 🚨 레슨 판정은 라벨 문자열이 아니라 `closureType` 원본 코드로 한다(라벨은 언어·문구가 바뀔 수 있다).
 */
export function boardCellKind(
  slot: Pick<ReservationBoardSlotView, 'state' | 'source' | 'closureType'>
): ReservationBoardCellKind | null {
  if (slot.state === 'RESERVED') return slot.source === 'MANUAL' ? 'MANUAL' : 'APP'
  if (slot.state === 'BLOCKED') return slot.closureType === 'LESSON' ? 'LESSON' : 'BLOCKED'
  return null
}
